import { useState, useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import Select from 'react-select';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Alert, Snackbar, Button, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import SecurityIcon from '@mui/icons-material/Security';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';

const RoleDetail = () => {
    useSessionValidation();

    const { id } = useParams();
    const history = useHistory();
    const { isAuthenticated, companyId, userId, logout, token } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, companyId]);

    const [selectedOption, setSelectedOption] = useState({ value: '1', label: 'Master' });
    const [formData, setFormData] = useState({ Name: '', Claims: [], UpdatedBy: userId });
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        fetchData('RoleApi', `/RoleDetails/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111' && response.response_data) {
                    const { RoleVM } = response.response_data;
                    setFormData((prev) => ({
                        ...prev,
                        ...RoleVM,
                        Claims: response.response_data.Claims
                            ? response.response_data.Claims.map((claim) => ({ ...claim }))
                            : [],
                    }));
                }
            })
            .catch((error) => {
                console.error('Error fetching Role Details:', error);
                setErrorMessage('Error fetching Role Details. Please try again.');
            });
    }, [id]);

    const getClaimRange = () => {
        const startIndex = (parseInt(selectedOption.value, 10) - 1) * 5;
        return { startIndex, endIndex: startIndex + 4 };
    };

    const handleChange = (index) => {
        const updatedClaims = [...formData.Claims];
        updatedClaims[index].IsSelected = !updatedClaims[index].IsSelected;
        setFormData((prev) => ({ ...prev, Claims: updatedClaims }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        updateData('RoleApi', `${id}`, formData, token)
            .then(() => {
                setSuccessMessage('Record updated successfully.');
                setErrorMessage('');
                history.push(`/roleDetail/${id}`);
            })
            .catch((error) => {
                console.error('Error updating record:', error);
                setErrorMessage('Error updating record. Please try again.');
            });
    };

    const { startIndex, endIndex } = getClaimRange();
    const categoryClaims = formData.Claims.filter((_, i) => i >= startIndex && i <= endIndex);
    const activeCategoryClaims = categoryClaims.filter(c => c.IsSelected);

    const sectionStyle = {
        border: '1px solid #e2e8f0', borderRadius: '10px',
        padding: '12px', marginBottom: '10px', background: '#fafafa'
    };

    const sectionTitleStyle = {
        display: 'flex', alignItems: 'center', gap: '6px',
        fontSize: '12px', fontWeight: '700', color: '#2C3E6E',
        textTransform: 'uppercase', letterSpacing: '0.5px',
        marginBottom: '10px', paddingBottom: '6px',
        borderBottom: '1px solid #e2e8f0'
    };

    const statBoxStyle = (bg) => ({
        background: bg, padding: '6px 10px', borderRadius: '6px',
        color: 'white', display: 'flex', alignItems: 'center',
        justifyContent: 'center', gap: '6px'
    });

    return (
        <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', color: 'black', fontFamily: 'Times New Roman' }}>

                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
                    <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, fontFamily: 'Times New Roman', textTransform: 'uppercase', color: 'black' }}>
                        Role Details
                    </h4>
                    <div style={{ display: 'flex', gap: '6px' }}>
                        <Tooltip title="Save changes" arrow>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                startIcon={<SaveIcon sx={{ fontSize: '16px' }} />}
                                sx={{
                                    background: '#2C3E6E', color: 'white', padding: '4px 12px',
                                    borderRadius: '6px', fontSize: '12px', fontWeight: '700',
                                    minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap'
                                }}
                            >
                                Update
                            </Button>
                        </Tooltip>
                        <Button
                            variant="outlined"
                            component={Link}
                            to="/RoleIndex"
                            startIcon={<ArrowBackIcon sx={{ fontSize: '16px' }} />}
                            sx={{
                                borderColor: '#2C3E6E', color: '#2C3E6E', padding: '4px 12px',
                                borderRadius: '6px', fontSize: '12px', fontWeight: '700',
                                minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap'
                            }}
                        >
                            Back
                        </Button>
                    </div>
                </div>

                {/* Role Information */}
                <div style={sectionStyle}>
                    <div style={sectionTitleStyle}>
                        <AdminPanelSettingsIcon style={{ fontSize: '14px', color: '#2C3E6E' }} />
                        Role Information
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#4a5568', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                            Role Name
                        </label>
                        <input
                            value={formData.Name}
                            name="Name"
                            type="text"
                            placeholder="Role Name"
                            readOnly
                            style={{
                                width: '100%', padding: '6px 10px', fontSize: '13px',
                                borderRadius: '8px', border: '1px solid #d0d7e3',
                                background: '#f8f9fa', outline: 'none', fontFamily: 'inherit',
                                cursor: 'not-allowed', color: '#4a5568', boxSizing: 'border-box'
                            }}
                        />
                    </div>
                </div>

                {/* Claims Management */}
                <div style={sectionStyle}>
                    <div style={{ ...sectionTitleStyle, justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <SecurityIcon style={{ fontSize: '14px', color: '#2C3E6E' }} />
                            Role Claims
                        </div>
                    </div>

                    {/* Stats */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '10px' }}>
                        <div style={statBoxStyle('#2C3E6E')}>
                            <div style={{ fontSize: '18px', fontWeight: '700' }}>{categoryClaims.length}</div>
                            <div style={{ fontSize: '10px', opacity: 0.9 }}>Category Claims</div>
                        </div>
                        <div style={statBoxStyle('linear-gradient(135deg, #11998e 0%, #38ef7d 100%)')}>
                            <div style={{ fontSize: '18px', fontWeight: '700' }}>{activeCategoryClaims.length}</div>
                            <div style={{ fontSize: '10px', opacity: 0.9 }}>Active Claims</div>
                        </div>
                    </div>

                    {/* Category Selector */}
                    <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#4a5568', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                            Select Claim Category
                        </label>
                        <Select
                            options={[
                                { value: '1', label: 'Master' },
                                { value: '2', label: 'Transaction' },
                                { value: '3', label: 'Accounts' },
                                { value: '4', label: 'Reports' },
                                { value: '5', label: 'Administration' },
                            ]}
                            value={selectedOption}
                            onChange={setSelectedOption}
                            styles={{
                                control: (base, state) => ({
                                    ...base,
                                    borderColor: state.isFocused ? '#2C3E6E' : '#d0d7e3',
                                    borderWidth: '1px',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    minHeight: '34px',
                                    boxShadow: 'none',
                                    '&:hover': { borderColor: '#2C3E6E' },
                                    background: '#f8f9fa'
                                }),
                                option: (base, state) => ({
                                    ...base,
                                    backgroundColor: state.isSelected ? '#2C3E6E' : state.isFocused ? '#edf2f7' : 'white',
                                    color: state.isSelected ? 'white' : '#2d3748',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    padding: '6px 10px'
                                })
                            }}
                        />
                    </div>

                    {/* Claims List */}
                    <div style={{ maxHeight: '280px', overflowY: 'auto', background: 'white', borderRadius: '8px', padding: '8px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '11px', fontWeight: '600', color: '#4a5568', marginBottom: '6px' }}>
                            {selectedOption.label} Category:
                        </div>
                        {formData.Claims.length > 0 ? (
                            formData.Claims.map((claim, index) => {
                                if (index < startIndex || index > endIndex) return null;
                                return (
                                    <div
                                        key={index}
                                        onClick={() => handleChange(index)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '8px',
                                            padding: '8px 10px', marginBottom: '4px',
                                            background: claim.IsSelected ? 'rgba(44,62,110,0.07)' : 'white',
                                            borderRadius: '6px',
                                            border: claim.IsSelected ? '1px solid #2C3E6E' : '1px solid #e2e8f0',
                                            cursor: 'pointer', transition: 'all 0.2s ease'
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={claim.IsSelected}
                                            onChange={() => handleChange(index)}
                                            onClick={(e) => e.stopPropagation()}
                                            style={{ width: '15px', height: '15px', cursor: 'pointer', accentColor: '#2C3E6E' }}
                                        />
                                        <label style={{
                                            fontSize: '12px', fontWeight: claim.IsSelected ? '600' : '500',
                                            color: claim.IsSelected ? '#2C3E6E' : '#2d3748',
                                            flex: 1, cursor: 'pointer', margin: 0
                                        }}>
                                            {claim.ClaimValue}
                                        </label>
                                        {claim.IsSelected && (
                                            <CheckCircleIcon style={{ fontSize: '14px', color: '#2C3E6E' }} />
                                        )}
                                        <input type="hidden" name={`Claims[${index}].ClaimType`} value={claim.ClaimType} />
                                        <input type="hidden" name={`Claims[${index}].ClaimValue`} value={claim.ClaimValue} />
                                        <input type="hidden" name={`Claims[${index}].LogInClaims`} value={claim.LogInClaims} />
                                    </div>
                                );
                            })
                        ) : (
                            <p style={{ fontSize: '11px', color: '#666', textAlign: 'center', padding: '15px', margin: 0 }}>
                                No claims available for this category.
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <Snackbar
                open={!!errorMessage || !!successMessage}
                autoHideDuration={4000}
                onClose={() => { setErrorMessage(''); setSuccessMessage(''); }}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => { setErrorMessage(''); setSuccessMessage(''); }} severity={errorMessage ? 'error' : 'success'}>
                    {errorMessage || successMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default RoleDetail;

